import React, { Suspense } from "react";
import styles from "../addToCart.module.scss";

import { fetchCartData } from "../cartRepo";
import { formatCurrency } from "../../../lib/common";
import { trapFocus } from "../../../js/lib/focus-trap";
import { SuspenseProps } from "../../../lib/react-utils";

interface JustAddedToCartProps {
  handleClick: () => void;
  justAdded: {
    name: string;
    imgURL: string;
    price: string;
  };
}
const JustAddedToCart: React.FC<JustAddedToCartProps> = (props) => {
  const productInfoRef = React.useRef<HTMLDivElement>(null);

  // Add focus trap to modal
  React.useEffect(() => {
    const container = productInfoRef.current;
    if (container) {
      const focusableElements = Array.from(
        container.querySelectorAll("a, button") as NodeListOf<HTMLElement>
      );
      const keyDownHandler = trapFocus(focusableElements);

      window.addEventListener("keydown", keyDownHandler);

      return () => {
        window.removeEventListener("keydown", keyDownHandler);
      };
    }
  }, []);

  const justAddedInfo = props.justAdded;

  const resource = fetchCartData();

  return (
    <div className={styles.productInfo} ref={productInfoRef}>
      <h3 className={styles.justAdded}>Added To Cart</h3>

      <p className={styles.productName}>{justAddedInfo.name}</p>
      <img
        className={styles.productImage}
        src={justAddedInfo.imgURL}
        alt={justAddedInfo.name}
        width="66"
        height="74"
      />
      <p className={styles.productPrice}>{justAddedInfo.price} USD</p>
      <p className={styles.subTotal}>
        Subtotal:{" "}
        <Suspense
          fallback={
            <span
              style={{ display: "inline-block" }}
              className={styles.loader}
            ></span>
          }
        >
          <Subtotal resource={resource} />
          
        </Suspense>
        <br/><span className="loris-app-only fee-message">10% processing fee will be applied at checkout</span>
      </p>
      <button
        type="button"
        tabIndex={0}
        className={styles.keepShopping}
        title="Close"
        onClick={props.handleClick}
      >
        <svg
          version="1.1"
          viewBox="0 0 210 297"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="m40.944 75.627c-2.2614-0.009597-4.5191 0.8475-6.2446 2.573-3.4509 3.4509-3.4271 9.031 0.053226 12.511l57.789 57.789-57.789 57.789c-3.4804 3.4804-3.5042 9.0604-0.053226 12.511 3.4509 3.4509 9.031 3.4271 12.511-0.05323l57.789-57.789 57.789 57.789c3.4804 3.4804 9.0604 3.5042 12.511 0.05323 3.4509-3.4509 3.4271-9.031-0.05323-12.511l-57.789-57.789 57.789-57.789c3.4804-3.4804 3.5042-9.0604 0.05323-12.511-3.4509-3.4509-9.031-3.4271-12.511 0.053227l-57.789 57.789-57.789-57.789c-1.7402-1.7402-4.0054-2.6166-6.2668-2.6262z" />
        </svg>
      </button>

      <a className={styles.viewCart} tabIndex={0} href="/ShoppingCart.aspx">
        View Cart
        <Suspense
          fallback={
            <span
              style={{
                display: "inline-block",
                borderTopColor: "#fff",
                borderBottomColor: "#fff",
              }}
              className={styles.loader}
            ></span>
          }
        >
          <NumberOfItems resource={resource} />
        </Suspense>
      </a>
    </div>
  );
};

const NumberOfItems: React.FC<SuspenseProps<ShoppingCart>> = (
  props: SuspenseProps<ShoppingCart>
) => {
  const cartInfo = props.resource.read();
  let count = 0;
  count = cartInfo.Items.reduce(
    (accumulator, currentValue) => accumulator + currentValue.Quantity,
    0
  );

  return <span>( {count} )</span>;
};

const Subtotal: React.FC<SuspenseProps<ShoppingCart>> = (
  props: SuspenseProps<ShoppingCart>
) => {
  const cartInfo = props.resource.read();

  return <span>{`${formatCurrency(cartInfo.MerchandiseTotal)} USD`}</span>
};

export default JustAddedToCart;
